'use client';

import React, { useEffect, useMemo, useRef } from 'react';

import { type DefaultValues, type FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';

import type { FieldConfig } from '@/types/forms';
import {
  withMarketingConsentDefaultValues,
  withMarketingConsentFields,
  withMarketingConsentSchema,
} from '@/lib/forms/marketingConsent';

import {
  DynamicFormContent,
  areRequiredFieldsFilled,
} from './DynamicFormContent';
import { DynamicFormModal } from './DynamicFormModal';
import {
  useDynamicForm,
  type DynamicFormContext,
} from './hooks/useDynamicForm';

type FooterLink = {
  label: string;
  href: string;
};

type ModalConfig = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  closeButtonLabel?: string;
  className?: string;
  panelClassName?: string;
  backdropClassName?: string;
  autoCloseOnSuccessMs?: number | false;
};

type DynamicFormConfig<T extends FieldValues> = {
  fields: FieldConfig[];
  schema: ZodType<T>;
  defaultValues: DefaultValues<T>;
  columns?: 1 | 2;
  submitLabel?: string;
  footerLink?: FooterLink;
  buttonClassName?: string;
  buttonPaddingClassName?: string;
  buttonContainerClassName?: string;
};

type SecurityConfig = {
  honeypot?: boolean;
  honeypotName?: string;
};

type DynamicFormProps<T extends FieldValues> = {
  config?: DynamicFormConfig<T>;
  schema?: ZodType<T>;
  fields?: FieldConfig[];
  defaultValues?: DefaultValues<T>;

  columns?: 1 | 2;
  submitLabel?: string;
  footerLink?: FooterLink;
  buttonClassName?: string;
  buttonPaddingClassName?: string;
  buttonContainerClassName?: string;

  mode?: 'inline' | 'modal';

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: ModalConfig;

  security?: SecurityConfig;
  showMarketingConsent?: boolean;

  onSubmit?: (values: T, context?: DynamicFormContext) => void | Promise<void>;
  onAllFieldsFilled?: () => void;
  className?: string;
  renderFooterExtra?: (values: T) => React.ReactNode;
  disableSubmitUntilFilled?: boolean;
  suppressSuccess?: boolean;
};

function normalizeConfig<T extends FieldValues>(
  props: Pick<
    DynamicFormProps<T>,
    | 'config'
    | 'schema'
    | 'fields'
    | 'defaultValues'
    | 'columns'
    | 'submitLabel'
    | 'footerLink'
    | 'buttonClassName'
    | 'buttonPaddingClassName'
    | 'buttonContainerClassName'
  >,
): {
  fields: FieldConfig[];
  schema: ZodType<T>;
  defaultValues: DefaultValues<T>;
  columns: 1 | 2;
  submitLabel: string;
  footerLink: FooterLink | undefined;
  buttonClassName: string | undefined;
  buttonPaddingClassName: string | undefined;
  buttonContainerClassName: string;
} {
  if (props.config) {
    return {
      fields: props.config.fields,
      schema: props.config.schema,
      defaultValues: props.config.defaultValues,
      columns: props.config.columns ?? 2,
      submitLabel: props.config.submitLabel ?? 'Submit',
      footerLink: props.config.footerLink,
      buttonClassName: props.config.buttonClassName,
      buttonPaddingClassName: props.config.buttonPaddingClassName,
      buttonContainerClassName:
        props.config.buttonContainerClassName ?? 'flex items-center gap-4 pt-1',
    };
  }

  if (!props.schema || !props.fields || !props.defaultValues) {
    throw new Error(
      'DynamicForm: either provide a "config" prop, or provide "schema", "fields", and "defaultValues" props.',
    );
  }

  return {
    fields: props.fields,
    schema: props.schema,
    defaultValues: props.defaultValues,
    columns: props.columns ?? 2,
    submitLabel: props.submitLabel ?? 'Submit',
    footerLink: props.footerLink,
    buttonClassName: props.buttonClassName,
    buttonPaddingClassName: props.buttonPaddingClassName,
    buttonContainerClassName:
      props.buttonContainerClassName ?? 'flex items-center gap-4 pt-1',
  };
}

const DEFAULT_HONEYPOT_NAME = 'lips-name';

export function DynamicForm<T extends FieldValues>({
  config,
  schema,
  fields,
  defaultValues,
  columns,
  submitLabel,
  footerLink,
  buttonClassName,
  buttonPaddingClassName,
  buttonContainerClassName,
  mode = 'inline',
  open,
  onOpenChange,
  modal,
  security,
  showMarketingConsent = false,
  onSubmit,
  onAllFieldsFilled,
  className = '',
  renderFooterExtra,
  disableSubmitUntilFilled = false,
  suppressSuccess = false,
}: DynamicFormProps<T>) {
  const honeypotRef = useRef<HTMLInputElement>(null);

  const enhancedOnSubmit = useMemo(() => {
    if (!onSubmit) return undefined;
    return async (values: T) => {
      if (security?.honeypot) {
        const name = security.honeypotName ?? DEFAULT_HONEYPOT_NAME;
        const ctx: DynamicFormContext = {
          security: {
            honeypotName: name,
            honeypot: honeypotRef.current?.value ?? '',
          },
        };
        await onSubmit(values, ctx);
      } else {
        await onSubmit(values);
      }
    };
  }, [onSubmit, security]);
  const {
    fields: normalizedFields,
    schema: normalizedSchema,
    defaultValues: normalizedDefaultValues,
    columns: normalizedColumns,
    submitLabel: normalizedSubmitLabel,
    footerLink: normalizedFooterLink,
    buttonClassName: normalizedButtonClassName,
    buttonPaddingClassName: normalizedButtonPaddingClassName,
    buttonContainerClassName: normalizedButtonContainerClassName,
  } = normalizeConfig({
    config,
    schema,
    fields,
    defaultValues,
    columns,
    submitLabel,
    footerLink,
    buttonClassName,
    buttonPaddingClassName,
    buttonContainerClassName,
  });

  const effectiveConfig = useMemo(() => {
    if (!showMarketingConsent) {
      return {
        schema: normalizedSchema,
        fields: normalizedFields,
        defaultValues: normalizedDefaultValues,
      };
    }

    return {
      schema: withMarketingConsentSchema(normalizedSchema),
      fields: withMarketingConsentFields(normalizedFields),
      defaultValues: withMarketingConsentDefaultValues(normalizedDefaultValues),
    };
  }, [
    normalizedDefaultValues,
    normalizedFields,
    normalizedSchema,
    showMarketingConsent,
  ]);

  const {
    control,
    errors,
    isSubmitting,
    watchedValues,
    success,
    submitError,
    handleFormSubmit,
    resetFormState,
  } = useDynamicForm({
    schema: effectiveConfig.schema as ZodType<T>,
    defaultValues: effectiveConfig.defaultValues as DefaultValues<T>,
    onSubmit: enhancedOnSubmit,
    suppressSuccess,
  });

  const allFieldsFilledRef = useRef(false);

  useEffect(() => {
    if (!onAllFieldsFilled) return;

    const filled = areRequiredFieldsFilled(
      effectiveConfig.fields,
      watchedValues,
    );

    if (filled && !allFieldsFilledRef.current) {
      allFieldsFilledRef.current = true;
      onAllFieldsFilled();
    }

    if (!filled) {
      allFieldsFilledRef.current = false;
    }
  }, [watchedValues, effectiveConfig.fields, onAllFieldsFilled]);

  const autoCloseOnSuccessMs = modal?.autoCloseOnSuccessMs ?? 5000;

  useEffect(() => {
    if (
      mode !== 'modal' ||
      !open ||
      !success ||
      autoCloseOnSuccessMs === false
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      onOpenChange?.(false);
    }, autoCloseOnSuccessMs);

    return () => window.clearTimeout(timer);
  }, [autoCloseOnSuccessMs, mode, onOpenChange, open, success]);

  useEffect(() => {
    if (mode !== 'modal' || open !== false) {
      return;
    }

    const timer = window.setTimeout(() => {
      resetFormState();
      if (honeypotRef.current) {
        honeypotRef.current.value = '';
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [mode, open, resetFormState]);

  const formContent = (
    <DynamicFormContent
      success={success}
      control={control}
      errors={errors}
      isSubmitting={isSubmitting}
      watchedValues={watchedValues}
      submitError={submitError}
      handleFormSubmit={handleFormSubmit}
      fields={effectiveConfig.fields}
      columns={normalizedColumns}
      submitLabel={normalizedSubmitLabel}
      footerLink={normalizedFooterLink}
      buttonClassName={normalizedButtonClassName}
      buttonPaddingClassName={normalizedButtonPaddingClassName}
      buttonContainerClassName={normalizedButtonContainerClassName}
      className={className}
      renderFooterExtra={renderFooterExtra}
      disableSubmitUntilFilled={disableSubmitUntilFilled}
      security={security}
      honeypotRef={honeypotRef}
    />
  );

  if (mode === 'inline') {
    return formContent;
  }

  if (mode === 'modal') {
    if (open === undefined || onOpenChange === undefined) {
      throw new Error(
        'DynamicForm: when mode="modal", "open" and "onOpenChange" props are required.',
      );
    }
    return (
      <DynamicFormModal open={open} onOpenChange={onOpenChange} modal={modal}>
        {formContent}
      </DynamicFormModal>
    );
  }

  return formContent;
}
